import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

const EMPTY = { current: "", next: "", confirm: "" };

export function ChangePasswordButton({ variant = "icon" }) {
  const { user, setUser } = useApp();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const mismatch = form.confirm && form.next !== form.confirm;
  const tooShort = form.next && form.next.length < 8;
  const canSubmit = form.current && form.next.length >= 8 && form.next === form.confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      setUser(data);
      toast.success("Password changed");
      setOpen(false);
      setForm(EMPTY);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const field = (key, label, testid) => (
    <div>
      <label className="text-xs font-semibold text-muted-foreground block mb-1">{label}</label>
      <Input type="password" autoComplete={key === "current" ? "current-password" : "new-password"} value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="h-9 text-sm" data-testid={testid} />
    </div>
  );

  return (
    <>
      {variant === "icon" ? (
        <Button variant="ghost" size="icon" className="relative" title="Change password" data-testid="change-password-btn" onClick={() => setOpen(true)}>
          <KeyRound size={15} />
          {user?.password_is_default && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-amber-500" data-testid="default-password-dot" />}
        </Button>
      ) : (
        <button data-testid="change-password-btn" onClick={() => setOpen(true)} className="rounded border px-2.5 py-1 hover:bg-secondary transition-colors duration-200 inline-flex items-center gap-1.5">
          <KeyRound size={12} /> Password
          {user?.password_is_default && <span className="h-2 w-2 rounded-full bg-amber-500" data-testid="default-password-dot" />}
        </button>
      )}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(EMPTY); }}>
        <DialogContent className="sm:max-w-sm" data-testid="change-password-dialog">
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Change password</DialogTitle>
              <DialogDescription>
                {user?.password_is_default
                  ? "You are still using the default password issued to you. Set a personal one now."
                  : "Choose a new password of at least 8 characters."}
              </DialogDescription>
            </DialogHeader>
            {field("current", "Current password", "current-password-input")}
            {field("next", "New password", "new-password-input")}
            {field("confirm", "Confirm new password", "confirm-password-input")}
            {(mismatch || tooShort) && (
              <p className="text-xs text-red-600" data-testid="change-password-hint">{tooShort ? "New password must be at least 8 characters." : "Passwords do not match."}</p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="change-password-cancel">Cancel</Button>
              <Button type="submit" disabled={!canSubmit} data-testid="change-password-submit">{busy ? "Saving…" : "Update password"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
